import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Drawer,
  Autocomplete,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Skeleton,
  Tooltip,
  FormControlLabel,
  CircularProgress,
  Collapse
} from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PeopleIcon from '@mui/icons-material/People';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import BranchIcon from '@mui/icons-material/AccountTree';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useLocation } from "react-router-dom";
import CustomPagination from "../../components/CustomPagination";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import QuestionPaperTemplate from "./anna-university-template/QuestionPaperTemplate";
import { buildPaperFromSections, buildPaperFromPreview } from "./create/paperBuilder";
import { TableLoader } from "../../components/TableLoader";
import { OverAllLoader } from "../../components/OverAllLoader";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const subcategories = [
  { category: 'Python Core', name: 'Basic Syntax', desc: 'Variables, data types, and operators in Python 3.x environment.' },
  { category: 'Python Core', name: 'Data Structures', desc: 'Detailed exploration of Lists, Tuples, Dictionaries, and Sets including memory management.' },
  { category: 'Advanced Python', name: 'Algorithms', desc: 'Implementation of sorting, searching, and recursion patterns.' },
  { category: 'Advanced Python', name: 'OOP Concepts', desc: 'Deep dive into Classes, inheritance, polymorphism, and encapsulation.' },
];

const assignedStudents = [
  { initials: 'AM', color: '#e3f2fd', textColor: '#1c97e3', name: 'Arjun Mehta', email: 'arjun.mehta@university.edu', phone: '+91 98765 43210', gender: 'Male' },
  { initials: 'SC', color: '#fce4ec', textColor: '#c2185b', name: 'Sara Chen', email: 'sara.chen@techinst.com', phone: '+1 415 555 0123', gender: 'Female' },
  { initials: 'BK', color: '#e8f5e9', textColor: '#10b981', name: 'Bamidele Koker', email: 'b.koker@academy.org', phone: '+234 803 123 4567', gender: 'Male' },
];

const assignedBatches = [
  { batch_id: 1, batch_name: 'BE CSE - sem 1', institute_name: 'Anna University', created_by_name: 'John Doe', created_at: '2023-10-15', status: 'Active' },
  { batch_id: 2, batch_name: 'BE CSE - sem 2', institute_name: 'Anna University', created_by_name: 'Jane Smith', created_at: '2023-10-20', status: 'Active' },
  { batch_id: 3, batch_name: 'BE IT - sem 1', institute_name: 'SRM University', created_by_name: 'Mike Johnson', created_at: '2023-10-25', status: 'Inactive' },
  { batch_id: 4, batch_name: 'BE IT - sem 2', institute_name: 'SRM University', created_by_name: 'Sarah Williams', created_at: '2023-11-01', status: 'Active' },
  { batch_id: 5, batch_name: 'MCA - sem 1', institute_name: 'VIT University', created_by_name: 'David Brown', created_at: '2023-11-05', status: 'Active' },
];

const systemStudentsList = ['Abhinav Singh', 'Hariharan A', 'Hrithik Singh', 'Karan R', 'Karthikumar B', 'Mahendra G'];

const QuestionPaperView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Support both paper_id (from QuestionPaperBuilder) and assessmentId (from Dashboard)
  const paperId = location.state?.paper_id || location.state?.assessmentId;
  const isFromDashboard = location.state?.dashboard === true;
  const { showSnackbar } = useSnackbar();
  
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [appliedCategory, setAppliedCategory] = useState(null);
  const [appliedGender, setAppliedGender] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(assignedBatches.length);

  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignSelectedStudents, setAssignSelectedStudents] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);

  // Pending students: added to an already-assigned batch after assignment, so they
  // were never snapshotted into this assessment. Reconcile via a checkbox dialog.
  const [pendingStudents, setPendingStudents] = useState([]);
  const [openPendingDialog, setOpenPendingDialog] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [syncingPending, setSyncingPending] = useState(false);
  const [showPendingPeek, setShowPendingPeek] = useState(false);

  const categoryOptions = ["Batch A", "Batch B", "Computer Science", "Information Technology"];
  const genderOptions = ["Male", "Female", "Other"];
  const [loading, setLoading] = useState(true);
  const [assignLoading, setAssignLoading] = useState(false);
  const [paperData, setPaperData] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [assignedBatchesData, setAssignedBatchesData] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearch = useDebounce(searchKey, 500);
  const [allowReview, setAllowReview] = useState(false);
  const [togglingReview, setTogglingReview] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  // RANDOM: live preview built from /PreviewRandomPaper using the saved pool mappings
  const [randomPreviewPaper, setRandomPreviewPaper] = useState(null);
  const [randomPreviewLoading, setRandomPreviewLoading] = useState(false);
  const isRandom = paperData?.paper_type === "RANDOM";
  // Assign/unassign is only allowed while SCHEDULED or READY. Any other status
  // (OPEN/COMPLETED/CANCELLED, or anything new) is rejected by the backend — lock the
  // UI too. An absent status (still loading) is left unlocked; the backend guards it.
  const paperStatusValue = (paperData?.status || "").toUpperCase();
  const assignLocked =
    paperStatusValue !== "" && !["SCHEDULED", "READY"].includes(paperStatusValue);
  // Reconcile (assign/sync students) is only allowed on an APPROVED, still-assignable paper.
  const canAssign = paperData?.paper_status === "APPROVED" && !assignLocked;
  // Assign Batches button: disabled when locked, and also while SCHEDULED.
  const assignBtnDisabled = assignLocked || paperStatusValue === "SCHEDULED";
  const isSuperAdmin = localStorage.getItem("role") === "SUPER_ADMIN";
  const colSpan = 4 + (paperData?.test_type === 1 ? 1 : paperData?.test_type === 2 ? 2 : 0) + (isSuperAdmin ? 1 : 0);

  // Fetch assigned batches list
  const fetchAssignedBatches = async () => {
    if (!paperId) return;

    try {
      const payload = {
        assessment_id: paperId,
        page: page,
        size: rowsPerPage,
        search_key: debouncedSearch || null
      };

      const res = await api.post("ListBatchesAssignedToAssessment", payload);

      if (res?.data?.status === "success") {
        setAssignedBatchesData(res?.data?.data || []);
        setCount(res?.data?.count || 0);
      } else {
        setAssignedBatchesData([]);
        setCount(0);
      }
    } catch (error) {
      console.error("Error fetching assigned batches:", error);
      setAssignedBatchesData([]);
      setCount(0);
    }
  };

  // Fetch students who are in assigned batches but not yet part of this assessment.
  const fetchPendingStudents = async () => {
    if (!paperId) return;
    try {
      const res = await api.post("ListPendingStudentsForAssessment", { assessment_id: paperId });
      if (res?.data?.status === "success") {
        setPendingStudents(res?.data?.data || []);
      } else {
        setPendingStudents([]);
      }
    } catch (error) {
      console.error("Error fetching pending students:", error);
      setPendingStudents([]);
    }
  };

  // Fetch batch options for assign dialog
  React.useEffect(() => {
    const fetchBatchOptions = async () => {
      try {
        const { data } = await api.post("getAllBatchList");
        setBatchOptions(data?.data ?? []);
      } catch (error) {
        console.error("Error fetching batch options:", error);
      }
    };

    fetchBatchOptions();
  }, []);

  React.useEffect(() => {
    fetchAssignedBatches();
  }, [page, rowsPerPage, paperId, debouncedSearch]);

  React.useEffect(() => {
    fetchPendingStudents();
  }, [paperId]);

  const fetchQuestionPaper = React.useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const res = await api.post("ViewQuestionPaper", { assessment_id: paperId });
      console.log("API Response:", res?.data);
      setApiResponse(res?.data);
      if (res?.data?.status === "success") {
        setPaperData(res?.data?.data);
        setAllowReview(res?.data?.data?.marks_visible === "YES");
      } else {
        console.log("API Status not success:", res?.data?.status);
      }
    } catch (error) {
      console.error("Error fetching question paper:", error);
    } finally {
      if (!isRefresh) setLoading(false);
    }
  }, [paperId]);

  React.useEffect(() => {
    if (paperId) {
      fetchQuestionPaper();
    }
  }, [fetchQuestionPaper, paperId]);

  const handleEditNavigate = () => {
    navigate("/questionPaperBuilder/create", {
      state: {
        assessment_id: paperId,
        edit: true,
      },
    });
  };

  // Build the printable question-paper from the ViewQuestionPaper response
  // (header + sections + questions, including images).
  const templatePaper = React.useMemo(() => {
    if (!paperData?.sections?.length && !paperData?.header) return null;
    const h = paperData?.header || {};
    const header = {
      university: h.university,
      examTitle: h.exam_title,
      collegeName: h.college_name,
      department: h.department,
      subjectCode: h.subject_code,
      subjectName: h.subject_name,
      semester: h.semester,
      regulation: h.regulation,
      academicYear: h.academic_year,
      time: h.time,
      maxMarks: h.max_marks,
      footerNote: h.footer_note,
    };
    const slot = (q) => (q ? { text: q.question, options: q.options, image_url: q.image_url, marks: q.marks } : null);
    const viewSections = (paperData.sections || []).map((s) => {
      const splitMark = s.split_mark || [];
      const isSplit = splitMark.length > 1;
      const qs = [...(s.questions || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      const perQ = splitMark.length
        ? splitMark.reduce((a, b) => a + b, 0)
        : qs[0]?.marks || s.marks_per_question || 0;
      let items = [];
      if (s.pattern === "either-or") {
        // split_mark length > 1 → each option (a/b) is a group of sub-questions (i, ii, …).
        const groupSize = isSplit ? splitMark.length : 1;
        if (qs.some((q) => q.pair_id != null)) {
          const byPair = {};
          const order = [];
          qs.forEach((q) => {
            if (!(q.pair_id in byPair)) { byPair[q.pair_id] = { a: [], b: [] }; order.push(q.pair_id); }
            const label = (q.pair_label || "a").toLowerCase() === "b" ? "b" : "a";
            byPair[q.pair_id][label].push(slot(q));
          });
          items = order.map((pid) => {
            const { a, b } = byPair[pid];
            return isSplit ? { a: { subs: a }, b: { subs: b } } : { a: a[0] || null, b: b[0] || null };
          });
        } else {
          // No pairing info: questions arrive as option a's sub(s), then option b's sub(s), per slot.
          const perSlot = groupSize * 2;
          for (let i = 0; i < qs.length; i += perSlot) {
            const chunk = qs.slice(i, i + perSlot);
            items.push(
              isSplit
                ? { a: { subs: chunk.slice(0, groupSize).map(slot) }, b: { subs: chunk.slice(groupSize, perSlot).map(slot) } }
                : { a: slot(chunk[0]), b: slot(chunk[1]) }
            );
          }
        }
      } else if (isSplit) {
        for (let i = 0; i < qs.length; i += splitMark.length) {
          items.push({ subs: qs.slice(i, i + splitMark.length).map(slot) });
        }
      } else {
        items = qs.map(slot);
      }
      return {
        title: s.section_name,
        pattern: s.pattern,
        // Number of rendered slots — for either-or / split this differs from no_of_questions
        // (which counts sub-questions), so derive it from what we actually built.
        count: items.length,
        marks: perQ,
        start: s.start_number,
        splitMark,
        instructions: s.instructions || [],
        choice: s.choice || null,
        items,
      };
    });
    return buildPaperFromSections(viewSections, header);
  }, [paperData]);

  // RANDOM — toggle a live preview built from the saved pool mappings via /PreviewRandomPaper.
  const handleViewRandomTemplate = async () => {
    if (showTemplate) { setShowTemplate(false); return; }
    setRandomPreviewLoading(true);
    try {
      const section_pools = [];
      (paperData?.sections || []).forEach((s, si) =>
        (s.pools || []).forEach((p) => {
          section_pools.push({
            section_index: s.section_order != null ? s.section_order : si,
            session_key: p.session_key,
            no_of_questions: p.no_of_questions,
            marks: p.marks ?? null,
            blooms_level: p.blooms_level ?? null,
          });
        })
      );
      const res = await api.post("PreviewRandomPaper", {
        template_id: paperData?.template_id,
        category_id: paperData?.category_id,
        program_id: paperData?.program_id,
        academic_year: paperData?.academic_year || paperData?.header?.academic_year || "",
        exam_date: paperData?.exam_date || paperData?.scheduled_date || null,
        time_limit: String(paperData?.time_limit || ""),
        section_pools,
      });
      if (res?.data?.status === "success") {
        setRandomPreviewPaper(buildPaperFromPreview(res.data.data, {}));
        setShowTemplate(true);
      } else {
        showSnackbar("error", res?.data?.message || "Could not generate preview");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Could not generate preview");
    } finally {
      setRandomPreviewLoading(false);
    }
  };

  const handleToggleReview = async (event) => {
    const newStatus = event.target.checked ? "YES" : "NO";
    setAllowReview(event.target.checked);
    setTogglingReview(true);
    try {
      const payload = {
        assessment_id: paperId,
        visible: newStatus
      };
      const res = await api.post("ToggleMarksVisibility", payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message || "Assessment review visibility updated successfully");
        await fetchQuestionPaper(true);
      } else {
        showSnackbar("error", res?.data?.message || "Failed to update exam review visibility");
        setAllowReview(!event.target.checked);
      }
    } catch (error) {
      console.error("Error toggling review visibility:", error);
      showSnackbar("error", "Failed to update exam review visibility");
      setAllowReview(!event.target.checked);
    } finally {
      setTogglingReview(false);
    }
  };

  const handleOpenAssignDialog = () => {
    setSelectedBatches([]);
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
  };

  const handleAssign = async () => {
    if (selectedBatches.length === 0) {
      showSnackbar("error", "Please select at least one batch");
      return;
    }

    setAssignLoading(true);
    try {
      const batchIds = selectedBatches.map(batch => batch.batch_id);
      const payload = {
        batch_ids: batchIds,
        assessment_id: paperId
      };

      const res = await api.post("BatchesAssessmentList", payload);

      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message || "Batches assigned successfully");
        handleCloseAssignDialog();
        // Refresh the assigned batches list + pending students
        fetchAssignedBatches();
        fetchPendingStudents();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to assign batches");
      }
    } catch (error) {
      console.error("Error assigning batches:", error);
      showSnackbar("error", "Failed to assign batches");
    } finally {
      setAssignLoading(false);
    }
  };

  // ---- Pending students reconcile (checkbox dialog) ----
  const handleOpenPendingDialog = () => {
    // default every pending student to checked
    setSelectedPendingIds(pendingStudents.map((s) => s.student_id));
    setOpenPendingDialog(true);
  };

  const handleClosePendingDialog = () => {
    setOpenPendingDialog(false);
  };

  const handleTogglePending = (studentId) => {
    setSelectedPendingIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleToggleSelectAllPending = (checked) => {
    setSelectedPendingIds(checked ? pendingStudents.map((s) => s.student_id) : []);
  };

  const handleSyncPending = async () => {
    if (selectedPendingIds.length === 0) {
      showSnackbar("error", "Please select at least one student");
      return;
    }
    setSyncingPending(true);
    try {
      const res = await api.post("SyncPendingStudentsToAssessment", {
        assessment_id: paperId,
        student_ids: selectedPendingIds,
      });
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message || "Students added to the assessment");
        handleClosePendingDialog();
        fetchAssignedBatches();
        fetchPendingStudents();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to add students");
      }
    } catch (error) {
      console.error("Error syncing pending students:", error);
      showSnackbar("error", error?.response?.data?.message || "Failed to add students");
    } finally {
      setSyncingPending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 4 }, backgroundColor: "#fafbfc" }}>
      <Box sx={{ width: "100%", margin: "0 auto" }}>

        {/* Header */}
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>
            Back
          </Typography>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            {loading ? (
              <Skeleton variant="text" width={300} height={48} />
            ) : (
              <>
                <Typography sx={{ fontSize: "32px", fontWeight: 800, color: "#1c2434" }}>
                  {paperData?.category_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                  {paperData?.paper_type && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        fontWeight: 800,
                        fontSize: "11px",
                        letterSpacing: 0.5,
                        backgroundColor: isRandom ? "#eef2ff" : "#ecfeff",
                        color: isRandom ? "#4f46e5" : "#0e7490",
                        border: isRandom ? "1px solid #c7d2fe" : "1px solid #a5f3fc",
                      }}
                    >
                      {isRandom ? "◆ RANDOM" : "● FIXED"}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 0.5,
                      borderRadius: "12px",
                      fontWeight: "bold",
                      fontSize: "12px",
                      backgroundColor:
                        paperData?.status === "OPEN"
                          ? "#e8f5e9"
                          : paperData?.status === "SCHEDULED"
                            ? "#e3f2fd"
                            : paperData?.status === "CLOSED"
                              ? "#ffebee"
                              : paperData?.status === "COMPLETED"
                                ? "#f3e5f5"
                                : paperData?.status === "CANCELLED"
                                  ? "#fce4ec"
                                  : "#f1f1f1",
                      color:
                        paperData?.status === "OPEN"
                          ? "#388e3c"
                          : paperData?.status === "SCHEDULED"
                            ? "#1976d2"
                            : paperData?.status === "CLOSED"
                              ? "#d32f2f"
                              : paperData?.status === "COMPLETED"
                                ? "#7b1fa2"
                                : paperData?.status === "CANCELLED"
                                  ? "#c2185b"
                                  : "#333",
                      textAlign: "center",
                      textTransform: "capitalize",
                    }}
                  >
                    {paperData?.status
                      ? paperData.status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
                      : "-"}
                  </Box>
                  {paperData?.scheduled_date && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontSize: '12px' }}>
                      <CalendarTodayIcon sx={{ fontSize: '14px' }} />
                      <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                        {new Date(paperData?.scheduled_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 2, alignItems: 'center', mt: 1 }}>
            {paperData?.test_type === 2 && paperData?.status === "COMPLETED" && (
              <Box
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  px: 2,
                  py: 0.5,
                  backgroundColor: 'white',
                }}
              >
                <FormControlLabel
                  control={
                    togglingReview ? (
                      <CircularProgress size={20} sx={{ m: 1 }} />
                    ) : (
                      <Checkbox
                        checked={allowReview}
                        onChange={handleToggleReview}
                        disabled={togglingReview}
                        color="primary"
                        size="small"
                      />
                    )
                  }
                  label={<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Allow Assessment Review</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEditNavigate}
              startIcon={<EditIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                padding: '8px 24px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Edit {getLabel('QUESTION_PAPER')}
            </Button>
            <Tooltip title={assignBtnDisabled ? `Batches can't be assigned while the assessment is ${paperStatusValue.toLowerCase()}.` : ""}>
              <span>
                <Button
                  variant="contained"
                  className={assignBtnDisabled ? "" : "gradient-button"}
                  onClick={handleOpenAssignDialog}
                  disabled={assignBtnDisabled}
                  startIcon={<PeopleIcon />}
                  sx={{
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    padding: '8px 24px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Assign {getLabel('BATCHES')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Description */}
        {/* <Typography sx={{ color: '#64748b', fontSize: '14px', maxWidth: '850px', mb: 5, lineHeight: 1.6 }}>
          This assessment covers core Python programming concepts including syntax, data structures, and object-oriented principles. Created on <Box component="span" sx={{ fontWeight: 600, color: '#1c2434' }}>Oct 24, 2023</Box> by <Box component="span" sx={{ fontWeight: 600, color: '#1c2434' }}>Sarah Jenkins</Box>.
        </Typography> */}

        {/* Summary Cards */}
        <Grid sx={{ display: "flex", justifyContent: "space-between", mb: 10, gap: 2 }}>
          {Array.from(new Array(4)).map((_, i) => (
            <Grid key={i} sx={{ width: '100%' }}>
              <Card sx={{
                p: 3,
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                border: 'none',
                borderLeft: `4px solid ${i === 0 ? '#1c97e3' : i === 1 ? '#10b981' : i === 2 ? '#f59e0b' : '#d32f2f'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '100%',
                backgroundColor: 'white'
              }}>
                {loading ? (
                  <>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" height={40} />
                  </>
                ) : i === 0 ? (
                  <>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#e9f5fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <HelpOutlineIcon sx={{ color: '#1c97e3', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E9EBC', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px', mb: 0.5 }}>NO. OF QUESTIONS</Typography>
                      <Typography sx={{ color: '#1c2434', fontSize: '32px', fontWeight: 800 }}>{paperData?.no_of_questions || 0}</Typography>
                    </Box>
                  </>
                ) : i === 1 ? (
                  <>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#ebfcf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AssignmentTurnedInOutlinedIcon sx={{ color: '#10b981', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E9EBC', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px', mb: 0.5 }}>EXAM TYPE</Typography>
                      <Typography sx={{ color: '#1c2434', fontSize: '30px', fontWeight: 800, lineHeight: 1.1 }}>
                        {paperData?.test_type === 1 ? 'Self' : 'Proctored'}<br />assessment
                      </Typography>
                    </Box>
                  </>
                ) : i === 2 ? (
                  <>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#fff8eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TimerOutlinedIcon sx={{ color: '#f59e0b', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E9EBC', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px', mb: 0.5 }}>TIME DURATION</Typography>
                      <Typography sx={{ color: '#1c2434', fontSize: '32px', fontWeight: 800 }}>
                        {/^\d+$/.test(String(paperData?.time_limit || "")) ? `${paperData?.time_limit} Min` : (paperData?.time_limit || "-")}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: paperData?.camera_assigned_status === "YES" ? '#e8f5e9' : '#ffebee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <VideoCameraFrontIcon sx={{ color: paperData?.camera_assigned_status === "YES" ? '#388e3c' : '#d32f2f', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E9EBC', fontSize: '14px', fontWeight: 600, letterSpacing: '0.2px', mb: 0.5 }}>CAMERA STATUS</Typography>
                      <Typography sx={{ color: '#1c2434', fontSize: '30px', fontWeight: 800, lineHeight: 1.1 }}>
                        {paperData?.camera_assigned_status === "YES" ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Curriculum Weightage (RANDOM papers — driven by subcategory weightage) */}
        {(paperData?.subcategories?.length > 0 || loading) && (
          <Box sx={{ mb: 6, mt: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#1c2434' }}>
                Curriculum Weightage
              </Typography>
              <Chip
                label={`${paperData?.subcategories?.length || 0} SECTIONS`}
                size="small"
                sx={{
                  backgroundColor: '#e9f5fd',
                  color: '#1c97e3',
                  fontWeight: 700,
                  fontSize: '11px',
                  borderRadius: '8px',
                }}
              />
            </Box>

            <Box sx={{ backgroundColor: "white", borderRadius: "12px", border: "1px solid #f1f3f4", boxShadow: '0 2px 10px rgba(0,0,0,0.02)', overflow: "hidden" }}>
              {/* Header */}
              <Box sx={{ display: "flex", backgroundColor: "#f8f9fa", borderBottom: "1px solid #f1f3f4", py: 2, px: 3 }}>
                <Box sx={{ flex: 3 }}>
                  <Typography sx={{ fontWeight: 600, color: "#374151" }}>Subtopic</Typography>
                </Box>
                <Box sx={{ flex: 2 }}>
                  <Typography sx={{ fontWeight: 600, color: "#374151" }}>Type</Typography>
                </Box>
                {/* <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 600, color: "#374151" }}>Questions</Typography>
              </Box> */}
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 600, color: "#374151" }}>Weightage</Typography>
                </Box>
              </Box>

              {/* Table Body */}
              {loading ? (
                <Box sx={{ py: 5 }}>
                  <TableLoader message="Fetching details..." />
                </Box>
              ) : (
                paperData?.subcategories?.map((item, index) => (
                  <Box key={index} sx={{
                    display: "flex",
                    borderBottom: index === (paperData?.subcategories?.length || 0) - 1 ? "none" : "1px solid #f1f3f4",
                    py: 2,
                    px: 3,
                    "&:hover": {
                      backgroundColor: "#fafafb"
                    }
                  }}>
                    <Box sx={{ flex: 3 }}>
                      <Typography sx={{ fontWeight: 500, color: "#1f2937" }}>
                        {item.subcategory_name}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 2 }}>
                      <Chip
                        label={item.question_type || "N/A"}
                        size="small"
                        sx={{
                          backgroundColor: "#f3f4f6",
                          color: "#6b7280",
                          fontWeight: 600,
                          fontSize: "11px",
                          borderRadius: "4px"
                        }}
                      />
                    </Box>
                    {/* <Box sx={{ flex: 1, textAlign: "center" }}>
                      <Typography sx={{ color: "#4b5563", fontWeight: 500 }}>
                        N/A
                      </Typography>
                    </Box> */}
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <Box sx={{
                        width: "40px",
                        height: "8px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        overflow: "hidden"
                      }}>
                        <Box
                          sx={{
                            width: `${item.percentage || 0}%`,
                            height: "100%",
                            backgroundColor: "#1c97e3",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>
                      <Typography sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1c97e3",
                        minWidth: "35px"
                      }}>
                        {item.percentage || 0}%
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}

        {/* RANDOM — saved pool mappings per section (read-only) + live template preview */}
        {!loading && isRandom && paperData?.sections?.length > 0 && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#0a0a0a' }}>Question Pools</Typography>
                {paperData?.template_name && (
                  <Chip label={paperData.template_name} size="small" sx={{ backgroundColor: '#e9f5fd', color: '#1c97e3', fontWeight: 700, fontSize: '11px', borderRadius: '8px' }} />
                )}
                {paperData?.paper_status && (
                  <Chip label={paperData.paper_status} size="small" sx={{ backgroundColor: paperData.paper_status === "APPROVED" ? '#e8f5e9' : '#fff3e0', color: paperData.paper_status === "APPROVED" ? '#388e3c' : '#f57c00', fontWeight: 700, fontSize: '11px', borderRadius: '8px' }} />
                )}
              </Box>
              <Button
                variant={showTemplate ? "outlined" : "contained"}
                onClick={handleViewRandomTemplate}
                disabled={randomPreviewLoading}
                startIcon={showTemplate ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                className={showTemplate ? "" : "gradient-button"}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: showTemplate ? '#1c97e3' : 'white', whiteSpace: 'nowrap', padding: '8px 24px' }}
              >
                {randomPreviewLoading ? "Drawing…" : showTemplate ? `Hide ${getLabel('QUESTION_PAPER')}` : `View ${getLabel('TEMPLATE')}`}
              </Button>
            </Box>

            {/* one card per section, listing its pools */}
            {(paperData.sections || []).map((s, si) => (
              <Box key={s.paper_section_id || si} sx={{ backgroundColor: "white", border: "1px solid #f1f3f4", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)", overflow: "hidden", mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f8f9fa", borderBottom: "1px solid #f1f3f4", py: 1.5, px: 3, flexWrap: "wrap", gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: "#1c2434" }}>{s.section_name}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip size="small" label={s.pattern === "list" ? "LIST" : "EITHER-OR"} sx={{ height: 20, fontSize: 10, fontWeight: 800, backgroundColor: s.pattern === "list" ? "#e0f2fe" : "#f3e8ff", color: s.pattern === "list" ? "#075985" : "#6b21a8" }} />
                    <Typography sx={{ fontSize: "12.5px", color: "#64748b" }}>{s.no_of_questions} questions · {s.marks_per_question} marks each</Typography>
                  </Box>
                </Box>
                {(s.pools || []).map((p, pi) => (
                  <Box key={p.mapping_id || pi} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 3, borderBottom: pi === (s.pools.length - 1) ? "none" : "1px solid #f1f3f4", flexWrap: "wrap" }}>
                    <Typography sx={{ flex: 3, minWidth: 160, fontWeight: 500, color: "#1f2937" }}>{p.subcategory_name}</Typography>
                    <Chip size="small" label={p.question_type || "—"} sx={{ width: 100, height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#e3f2fd", color: "#1c97e3", "& .MuiChip-label": { px: 0 } }} />
                    <Chip size="small" label={p.level || "—"} sx={{ width: 90, height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#fef3c7", color: "#d97706", "& .MuiChip-label": { px: 0 } }} />
                    <Chip size="small" label={p.marks != null ? `${p.marks}m` : "—"} sx={{ width: 44, height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#ede9fe", color: "#7c3aed", "& .MuiChip-label": { px: 0 } }} />
                    <Chip size="small" label={p.blooms_level || "—"} sx={{ width: 90, height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#dcfce7", color: "#16a34a", "& .MuiChip-label": { px: 0 } }} />
                    <Typography sx={{ flex: 1, textAlign: "right", fontSize: "12.5px", color: "#64748b", minWidth: 110 }}>{p.no_of_questions} of {p.available_questions} available</Typography>
                  </Box>
                ))}
                {(!s.pools || s.pools.length === 0) && (
                  <Box sx={{ py: 2, px: 3 }}>
                    <Typography sx={{ fontSize: "13px", color: "#b45309" }}>No pools assigned to this section.</Typography>
                  </Box>
                )}
              </Box>
            ))}

            <Collapse in={showTemplate} unmountOnExit>
              {randomPreviewPaper && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: "12.5px", color: "#7b8794", mb: 1, textAlign: "center" }}>
                    Live random draw — {getLabel('STUDENT')}s get a fresh draw each attempt.
                  </Typography>
                  <QuestionPaperTemplate data={randomPreviewPaper} />
                </Box>
              )}
            </Collapse>
          </Box>
        )}

        {/* Question Paper Template — FIXED papers; collapsible; expand to view */}
        {!isRandom && !loading && templatePaper && paperData?.sections?.length > 0 && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#0a0a0a' }}>
                  {getLabel('QUESTION_PAPER')} {getLabel('TEMPLATE')}
                </Typography>
                {paperData?.template_name && (
                  <Chip label={paperData.template_name} size="small" sx={{ backgroundColor: '#e9f5fd', color: '#1c97e3', fontWeight: 700, fontSize: '11px', borderRadius: '8px' }} />
                )}
                {paperData?.paper_status && (
                  <Chip
                    label={paperData.paper_status}
                    size="small"
                    sx={{
                      backgroundColor: paperData.paper_status === "APPROVED" ? '#e8f5e9' : '#fff3e0',
                      color: paperData.paper_status === "APPROVED" ? '#388e3c' : '#f57c00',
                      fontWeight: 700, fontSize: '11px', borderRadius: '8px',
                    }}
                  />
                )}
              </Box>
              <Button
                variant={showTemplate ? "outlined" : "contained"}
                onClick={() => setShowTemplate((p) => !p)}
                startIcon={showTemplate ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                className={showTemplate ? "" : "gradient-button"}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: showTemplate ? '#1c97e3' : 'white', whiteSpace: 'nowrap', padding: '8px 24px' }}
              >
                {showTemplate ? `Hide ${getLabel('QUESTION_PAPER')}` : `View ${getLabel('QUESTION_PAPER')}`}
              </Button>
            </Box>
            <Collapse in={showTemplate} unmountOnExit>
              <QuestionPaperTemplate data={templatePaper} />
            </Collapse>
          </Box>
        )}

        {/* Assigned Batches */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BranchIcon sx={{ fontSize: "32px", color: "#1976d2" }} />
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#0a0a0a' }}>
              Assigned {getLabel('STUDENT')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={`Search ${getLabel('BATCHES')}...`}
              variant="outlined"
              className="custom-search-field"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Drift banner — students added to assigned batches but not yet in this assessment */}
        {pendingStudents.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 2,
              mb: 2,
              backgroundColor: '#f0f9ff',
              border: '1px solid #cce8fb',
              borderLeft: '4px solid #1c97e3',
              borderRadius: '12px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#e9f5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <InfoOutlinedIcon sx={{ color: '#1c97e3', fontSize: '20px' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography sx={{ fontSize: '14px', color: '#1c2434' }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{pendingStudents.length} student{pendingStudents.length === 1 ? '' : 's'}</Box>
                {pendingStudents.length === 1 ? ' was' : ' were'} added to assigned batches but {pendingStudents.length === 1 ? "hasn't" : "haven't"} been added to this assessment yet.
              </Typography>
              <Typography sx={{ fontSize: '12.5px', color: '#64748b', mt: 0.25 }}>
                {pendingStudents.length === 1 ? 'This student' : 'They'} won't be able to attempt until you add {pendingStudents.length === 1 ? 'them' : 'them all'}.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
              <Button
                variant="text"
                onClick={() => setShowPendingPeek((p) => !p)}
                startIcon={showPendingPeek ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#1c97e3', '&:hover': { backgroundColor: '#e9f5fd' } }}
              >
                {showPendingPeek ? 'Hide' : 'View'}
              </Button>
              <Tooltip
                title={canAssign ? '' : `Set the status to Ready (via Edit) to approve the paper before adding ${getLabel('STUDENT')}.`}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    className={canAssign ? 'gradient-button' : ''}
                    onClick={handleOpenPendingDialog}
                    disabled={!canAssign}
                    startIcon={<PersonAddAlt1Icon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      color: 'white',
                      padding: '8px 24px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Add {getLabel('STUDENT')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Inline peek — quick read of who's pending before committing */}
        <Collapse in={showPendingPeek && pendingStudents.length > 0} unmountOnExit>
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fbfdff', border: '1px solid #e3f2fd', borderRadius: '10px' }}>
            {pendingStudents.map((s, i) => (
              <Box
                key={s.student_id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  px: 0.5,
                  borderBottom: i === pendingStudents.length - 1 ? 'none' : '1px solid #eef2f6',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1c2434' }}>{s.student_name}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                  {s.batch_name}{s.added_at ? ` · added ${new Date(s.added_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={
                  colSpan
                }>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {appliedCategory && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{appliedCategory}</Typography>
                          <IconButton size="small" onClick={() => { setAppliedCategory(null); setSelectedCategory(null); }} sx={{ ml: 0.5 }}>
                            <CloseIcon sx={{ fontSize: '14px' }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedGender && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{appliedGender}</Typography>
                          <IconButton size="small" onClick={() => { setAppliedGender(null); setSelectedGender(null); }} sx={{ ml: 0.5 }}>
                            <CloseIcon sx={{ fontSize: '14px' }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton onClick={() => setOpenDrawer(true)} color="primary" className="more-filter-btn">
                        <FilterListIcon />
                        <Typography className="more-filter-text">More filter</Typography>
                      </IconButton>
                      {(appliedCategory || appliedGender) && (
                        <Button
                          variant="outlined"
                          className="clear-filter-btn"
                          onClick={() => { setSelectedCategory(null); setSelectedGender(null); setAppliedCategory(null); setAppliedGender(null); }}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow className="tableHeader">
                {[
                  'S.No',
                  `${getLabel('STUDENT')} Name`,
                  `${getLabel('BATCHES')} Name`,
                  ...(paperData?.test_type === 1 ? ['Attempt Count'] : paperData?.test_type === 2 ? ['Total Mark', `${getLabel('EXAM')} Status`] : []),
                  ...(localStorage.getItem("role") === "SUPER_ADMIN" ? [`${getLabel('INSTITUTE')} Name`] : []),
                  // 'Status',
                  'Action'
                ].map((heading, i) => (
                  <TableCell key={i} align={heading === 'S.No' || heading === 'Action' || heading === 'Attempt Count' || heading === 'Total Mark' ? 'left' : 'left'} className="tableHeader"><b>{heading}</b></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody className="tablebody">
              {loading ? null : assignedBatchesData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 10 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <SearchOffIcon sx={{ fontSize: 50, color: "#cbd5e1" }} />
                      <Box>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>No {getLabel('BATCHES')} Assigned</Typography>
                        <Typography sx={{ fontSize: "13px", color: "#64748b", mt: 0.5 }}>Currently, there are no batches assigned to this {getLabel('QUESTION_PAPER')}.</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {assignedBatchesData.map((batch, index) => (
                    <TableRow
                      key={`${batch.student_id}_${batch.batch_id}_${index}`}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#1c2434" }}>
                        {batch.student_name}
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        {batch.batch_name}
                      </TableCell>
                      {paperData?.test_type === 1 && (
                        <TableCell align="left">
                          <Box
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "8px",
                              fontWeight: 600,
                              fontSize: "12px",
                              width: "fit-content",
                              backgroundColor: "#e9f5fd",
                              color: "#1c97e3",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {batch.noofattempts} Attempt{(batch.noofattempts || 1) > 1 ? 's' : ''}
                          </Box>
                        </TableCell>
                      )}
                      {paperData?.test_type === 2 && (
                        <TableCell align="left">
                          <Chip
                            label={`${batch.proctored_assessment_mark || 0}/${batch.max_marks || 100}`}
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              px: 1,
                              py: 0.75,
                              borderRadius: "10px",
                              background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                              fontSize: "0.875rem",
                              letterSpacing: "0.5px",
                              cursor: "pointer",
                              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          />
                        </TableCell>
                      )}
                      {paperData?.test_type === 2 && (
                        <TableCell align="left">
                          <Box
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "8px",
                              fontWeight: 600,
                              fontSize: "12px",
                              width: "fit-content",
                              backgroundColor: batch.attempt_status === "COMPLETED" ? "#e8f5e9" : "#fff3e0",
                              color: batch.attempt_status === "COMPLETED" ? "#388e3c" : "#f57c00",
                              textTransform: "capitalize",
                            }}
                          >
                            {batch.attempt_status === "COMPLETED" ? "Completed" : "Not Yet"}
                          </Box>
                        </TableCell>
                      )}
                      {localStorage.getItem("role") === "SUPER_ADMIN" && (
                        <TableCell sx={{ color: "#475569" }}>
                          {batch.institute_name}
                        </TableCell>
                      )}
                      {/* <TableCell align="center">
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            width: "fit-content",
                            backgroundColor:
                              batch.student_status === "ACTIVE"
                                ? "#e8f5e9"
                                : batch.student_status === "INACTIVE"
                                ? "#ffebee"
                                : batch.student_status === "COMPLETED"
                                ? "#f3e5f5"
                                : batch.student_status === "PENDING"
                                ? "#fff3e0"
                                : "#f1f1f1",
                            color:
                              batch.student_status === "ACTIVE"
                                ? "#388e3c"
                                : batch.student_status === "INACTIVE"
                                ? "#d32f2f"
                                : batch.student_status === "COMPLETED"
                                ? "#7b1fa2"
                                : batch.student_status === "PENDING"
                                ? "#f57c00"
                                : "#333",
                            textAlign: "center",
                            textTransform: "capitalize",
                          }}
                        >
                          {batch.student_status
                            ? batch.student_status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
                            : "-"}
                        </Box>
                      </TableCell> */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {paperData?.test_type === 2 ? (
                          <Tooltip
                            title={batch.attempt_status !== "COMPLETED" ? "Assessment not completed yet" : ""}
                            arrow
                          >
                            <span>
                              <IconButton
                                color="primary"
                                disabled={batch.attempt_status !== "COMPLETED"}
                                sx={{ cursor: batch.attempt_status !== "COMPLETED" ? "not-allowed" : "pointer" }}
                                onClick={() =>
                                  navigate("/examOverview", {
                                    state: {
                                      assessmentId: paperId,
                                      attemptId: batch.attempt_id,
                                      studentId: batch.student_id,
                                      studentName: batch.student_name,
                                    },
                                  })
                                }
                              >
                                <ManageSearchOutlinedIcon />
                                <Typography
                                  color="primary"
                                  sx={{ fontSize: "12px", ml: "4px" }}
                                >
                                  Assessment Overview
                                </Typography>
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() =>
                              navigate("/examHistory", {
                                state: {
                                  studentid: batch.student_id,
                                  studentName: batch.student_name,
                                  testType: paperData?.test_type === 1 ? "Self Assessment" : "Proctored Assessment",
                                  category: paperData?.category_name,
                                  level: paperData?.level,
                                  testId: paperData?.test_type,
                                  // Filter attempts to this assessment only (row value, fall back to this paper).
                                  assessmentId: batch.assessment_id ?? paperId,
                                },
                              })
                            }
                          >
                            <HistoryIcon />
                            <Typography
                              color="primary"
                              sx={{ fontSize: "12px", ml: "4px" }}
                            >
                              See Attempts
                            </Typography>
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {((page === 0 && assignedBatchesData?.length > rowsPerPage - 1) || page !== 0) && (
                    <CustomPagination
                      page={page}
                      setPage={setPage}
                      count={count}
                      rowsPerPage={rowsPerPage}
                      colSpan={colSpan}
                    />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Assign Students Dialog */}
        <Dialog
          open={openAssignDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '12px' }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2.5,
            borderBottom: '1px solid #f1f3f6'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonAddAlt1Icon sx={{ color: '#1c97e3' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1c2434' }}>
                Assign Students
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseAssignDialog} sx={{ color: '#1c97e3' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3, pt: 4, }}>
            <Autocomplete
              multiple
              options={batchOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.batch_name || ""}
              value={selectedBatches}
              onChange={(event, newValue) => {
                setSelectedBatches(newValue);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    color="primary"
                  />
                  {option?.batch_name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Batches"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PeopleIcon sx={{ color: '#1c97e3', mr: 1, ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      paddingLeft: '12px !important'
                    }
                  }}
                />
              )}
            />
          </DialogContent>

          <DialogActions sx={{ borderTop: '1px solid #f1f3f6', p: 2.5, justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleCloseAssignDialog}
              startIcon={<CancelIcon sx={{ color: '#8E9EBC' }} />}
              sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '13px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssign}
              className="gradient-button"
              startIcon={<PersonAddAlt1Icon />}
              disabled={assignLoading || selectedBatches.length === 0}
              sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '13px', borderRadius: '4px', px: 3, color: 'white' }}
            >
              {assignLoading ? "Assigning..." : "Assign"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Pending Students Dialog (checkbox — only selected get added) */}
        <Dialog
          open={openPendingDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, borderBottom: '1px solid #f1f3f6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonAddAlt1Icon sx={{ color: '#1c97e3' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1c2434' }}>
                Add Students to Assessment
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClosePendingDialog} sx={{ color: '#1c97e3' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Typography sx={{ color: '#64748b', fontSize: '13px', mb: 2, mt: 1 }}>
              Select the students you want to add to this assessment. Only the checked students will be added.
            </Typography>

            {/* Select all */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.5, mb: 1, backgroundColor: '#f8fafc', border: '1px solid #f1f3f6', borderRadius: '8px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={pendingStudents.length > 0 && selectedPendingIds.length === pendingStudents.length}
                    indeterminate={selectedPendingIds.length > 0 && selectedPendingIds.length < pendingStudents.length}
                    onChange={(e) => handleToggleSelectAllPending(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1c2434' }}>Select all</Typography>}
                sx={{ m: 0 }}
              />
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
                {selectedPendingIds.length} of {pendingStudents.length} selected
              </Typography>
            </Box>

            {/* Student rows */}
            <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {pendingStudents.map((s) => {
                const checked = selectedPendingIds.includes(s.student_id);
                const initials = (s.student_name || '')
                  .split(' ')
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();
                return (
                  <Box
                    key={s.student_id}
                    onClick={() => handleTogglePending(s.student_id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1,
                      py: 1,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f8fafc' },
                    }}
                  >
                    <Checkbox
                      color="primary"
                      checked={checked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleTogglePending(s.student_id)}
                    />
                    <Avatar sx={{ width: 34, height: 34, fontSize: '13px', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#1c2434' }}>{s.student_name}</Typography>
                      <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                        {s.batch_name}{s.added_at ? ` · added ${new Date(s.added_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </DialogContent>

          <DialogActions sx={{ borderTop: '1px solid #f1f3f6', p: 2.5, justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleClosePendingDialog}
              startIcon={<CancelIcon sx={{ color: '#8E9EBC' }} />}
              sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '13px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSyncPending}
              className="gradient-button"
              startIcon={syncingPending ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PersonAddAlt1Icon />}
              disabled={syncingPending || selectedPendingIds.length === 0}
              sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '13px', borderRadius: '4px', px: 3, color: 'white' }}
            >
              {syncingPending ? 'Adding...' : `Add ${selectedPendingIds.length} Student${selectedPendingIds.length === 1 ? '' : 's'}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          PaperProps={{
            sx: {
              width: 320,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              height: "100vh",
              borderRadius: "12px 0 0 12px",
            },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon color="primary" />
              <Typography variant="h6" fontWeight={600} color="primary">
                Filters
              </Typography>
            </Box>
            <IconButton aria-label="Close filters" size="small" onClick={() => setOpenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>
              {getLabel('SUBJECT')}
            </Typography>
            <Autocomplete
              size="small"
              options={categoryOptions}
              value={selectedCategory}
              onChange={(event, newValue) => setSelectedCategory(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder={`All ${getLabel('SUBJECT')}s`} fullWidth variant="outlined" />
              )}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>
              Gender
            </Typography>
            <Autocomplete
              size="small"
              options={genderOptions}
              value={selectedGender}
              onChange={(event, newValue) => setSelectedGender(newValue)}
              renderInput={(params) => (
                <TextField {...params} placeholder="All Genders" fullWidth variant="outlined" />
              )}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="customButton"
            onClick={() => {
              setAppliedCategory(selectedCategory);
              setAppliedGender(selectedGender);
              setOpenDrawer(false);
            }}
            sx={{ mt: 2 }}
          >
            Apply Filters
          </Button>
        </Drawer>

      </Box>
    </Box>
  );
};

export default QuestionPaperView;
